/**
 * ChannelsSection — Settings → Flint AI ▸ Channels.
 *
 * Where the assistant reaches the user: WhatsApp (auto-linked to the login
 * number), plus Telegram / Slack and additional WhatsApp numbers via a 6-digit
 * self-link code (ADR-0018).
 *
 * The voice/reply language is NOT set here anymore — it's a single workspace
 * default on the Agent card (migration 0105). This section is purely about the
 * connection lifecycle: connect, add number, disconnect.
 *
 * Self-contained: owns its own tRPC queries/mutations so the page route only
 * has to drop it into the section stack.
 */
import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Alert, Button, ConfirmDialog, StatusPill, cn } from "@flint/ui";
import { MessageCircle, Send, Phone, Plus, Bot } from "lucide-react";
import { trpc } from "../../../lib/trpc.ts";
import { FormCard } from "../form-card.tsx";

type Platform = "telegram" | "slack" | "whatsapp";

interface PlatformDef {
  id: Platform;
  label: string;
  handle: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const PLATFORMS: Record<Platform, PlatformDef> = {
  telegram: {
    id: "telegram",
    label: "Telegram",
    handle: "@flint_flowtrail_bot",
    description: "Chat with Flint AI in Telegram — briefings, questions, approvals.",
    Icon: Send,
  },
  slack: {
    id: "slack",
    label: "Slack",
    handle: "Flint AI app",
    description: "Bring Flint AI into your Slack workspace.",
    Icon: MessageCircle,
  },
  whatsapp: {
    id: "whatsapp",
    label: "WhatsApp",
    handle: "WhatsApp Business",
    description: "Use Flint AI on WhatsApp — the channel your team already uses.",
    Icon: Phone,
  },
};

/** Last 10 digits — compares a stored 10-digit phone with an E.164 chat id. */
function last10(s: string): string {
  return s.replace(/\D/g, "").slice(-10);
}

/** Format a stored 10-digit Indian number for display. */
function formatPhone(local: string): string {
  const d = last10(local);
  return d.length === 10 ? `+91 ${d.slice(0, 5)} ${d.slice(5)}` : `+91 ${d}`;
}

export function ChannelsSection() {
  const utils = trpc.useUtils();
  const me = trpc.identity.me.useQuery({});
  const connections = trpc.botChannels.listConnections.useQuery({});
  const generateToken = trpc.botChannels.generateLinkToken.useMutation();
  const disconnect = trpc.botChannels.disconnect.useMutation({
    onSuccess: () => void utils.botChannels.listConnections.invalidate(),
  });

  const [linkState, setLinkState] = useState<{
    platform: Platform;
    token: string;
    expiresAt: string;
    deepLink: string | null;
  } | null>(null);

  const [confirmDisconnect, setConfirmDisconnect] = useState<{
    sessionId: string;
    label: string;
  } | null>(null);

  async function handleConnect(platform: Platform) {
    const result = await generateToken.mutateAsync({ platform });
    setLinkState({
      platform,
      token: result.token,
      expiresAt: result.expiresAt,
      deepLink: result.deepLink,
    });
    // Auto-refresh connections after 90 s to catch the bot completing the link.
    setTimeout(() => void utils.botChannels.listConnections.invalidate(), 90_000);
  }

  const allConnections = connections.data ?? [];
  const selfPhone = me.data?.user.phone ?? null;
  const connectedCount = allConnections.length;

  // Telegram / Slack: a single connection per platform, keyed by platform.
  const byPlatform = new Map(allConnections.map((c) => [c.platform, c]));

  // WhatsApp: the primary number (login number) is auto-linked; any other
  // numbers are explicit "Add number" connections, each its own row.
  const waSessions = allConnections.filter((c) => c.platform === "whatsapp");
  const primaryWa =
    selfPhone != null
      ? waSessions.find((c) => last10(c.platformChatId) === last10(selfPhone))
      : undefined;
  const extraWa = waSessions.filter((c) => c !== primaryWa);
  const isGeneratingWa =
    generateToken.isPending && generateToken.variables?.platform === "whatsapp";

  return (
    <FormCard
      id="channels"
      icon={<Bot size={14} strokeWidth={2} aria-hidden />}
      title="Channels"
      description="Where you chat with Flint AI. Connected channels run as your account — disconnect any added channel at any time."
      badge={
        <StatusPill tone={connectedCount > 0 ? "ok" : "muted"} size="xs">
          {connectedCount > 0 ? `${connectedCount} connected` : "None yet"}
        </StatusPill>
      }
      bodyClassName="p-0"
    >
      <div className="space-y-3 p-5">
        {connections.isError && (
          <Alert
            intent="bad"
            title="Couldn't load connections"
            description="Failed to fetch your connected channels. Refresh to retry."
          />
        )}

        {linkState && (
          <LinkCodeBanner
            platform={PLATFORMS[linkState.platform]}
            token={linkState.token}
            expiresAt={linkState.expiresAt}
            deepLink={linkState.deepLink}
            onDismiss={() => {
              setLinkState(null);
              void utils.botChannels.listConnections.invalidate();
            }}
          />
        )}

        <div className="divide-y divide-line rounded-md border border-line bg-paper">
          {/* Telegram + Slack — single explicit connection each. */}
          {(["telegram", "slack"] as const).map((id) => {
            const def = PLATFORMS[id];
            const conn = byPlatform.get(id);
            const isGenerating =
              generateToken.isPending && generateToken.variables?.platform === id;
            return (
              <ChannelRow
                key={id}
                Icon={def.Icon}
                label={def.label}
                connected={!!conn}
                subtext={
                  conn ? (
                    <>
                      {def.handle} · Last active {formatDate(conn.lastActiveAt)}
                    </>
                  ) : (
                    def.description
                  )
                }
                action={
                  conn ? (
                    <DisconnectButton
                      onClick={() => setConfirmDisconnect({ sessionId: conn.id, label: def.label })}
                    />
                  ) : (
                    <Button
                      type="button"
                      intent="primary"
                      size="sm"
                      disabled={isGenerating}
                      onClick={() => void handleConnect(id)}
                    >
                      {isGenerating ? "Generating…" : "Connect"}
                    </Button>
                  )
                }
              />
            );
          })}

          {/* WhatsApp — primary auto-linked + any additional numbers. */}
          <div className="space-y-3 px-4 py-3.5">
            <ChannelRowBody
              Icon={PLATFORMS.whatsapp.Icon}
              label={PLATFORMS.whatsapp.label}
              badge={<ConnectedPill />}
              subtext={
                selfPhone ? (
                  primaryWa ? (
                    <>
                      {formatPhone(selfPhone)} · your number · Last active{" "}
                      {formatDate(primaryWa.lastActiveAt)}
                    </>
                  ) : (
                    <>
                      {formatPhone(selfPhone)} · your number · always on — just message the bot
                    </>
                  )
                ) : (
                  PLATFORMS.whatsapp.description
                )
              }
              action={
                <span className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-muted">
                  Your number
                </span>
              }
            />

            {extraWa.map((conn) => (
              <ChannelRowBody
                key={conn.id}
                Icon={PLATFORMS.whatsapp.Icon}
                label="WhatsApp"
                badge={<ConnectedPill />}
                subtext={
                  <>
                    {formatPhone(conn.platformChatId)} · Last active {formatDate(conn.lastActiveAt)}
                  </>
                }
                action={
                  <DisconnectButton
                    onClick={() =>
                      setConfirmDisconnect({
                        sessionId: conn.id,
                        label: `WhatsApp (${formatPhone(conn.platformChatId)})`,
                      })
                    }
                  />
                }
              />
            ))}

            <button
              type="button"
              disabled={isGeneratingWa}
              onClick={() => void handleConnect("whatsapp")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5",
                "text-xs font-medium text-ink-2 transition-colors hover:bg-bg",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <Plus className="h-3.5 w-3.5" />
              {isGeneratingWa ? "Generating…" : "Add number"}
            </button>
          </div>
        </div>
      </div>

      {confirmDisconnect && (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open) setConfirmDisconnect(null);
          }}
          title={`Disconnect ${confirmDisconnect.label}?`}
          description="You'll no longer receive AI responses on this channel. Reconnect at any time."
          confirmLabel="Disconnect"
          intent="danger"
          onConfirm={() => {
            disconnect.mutate({ sessionId: confirmDisconnect.sessionId });
            setConfirmDisconnect(null);
          }}
        />
      )}
    </FormCard>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function ConnectedPill() {
  return (
    <StatusPill tone="ok" size="xs" dot>
      Connected
    </StatusPill>
  );
}

/** Icon + label + status + action row body (shared by all platforms). */
function ChannelRowBody({
  Icon,
  label,
  badge,
  subtext,
  action,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  badge: React.ReactNode;
  subtext: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-4">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-line bg-bg text-muted">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">{label}</span>
          {badge}
        </div>
        <p className="mt-0.5 truncate text-xs text-muted">{subtext}</p>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  );
}

/** Padded row (Telegram / Slack) wrapping the shared body. */
function ChannelRow({
  Icon,
  label,
  connected,
  subtext,
  action,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  label: string;
  connected: boolean;
  subtext: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3.5">
      <ChannelRowBody
        Icon={Icon}
        label={label}
        badge={
          connected ? (
            <ConnectedPill />
          ) : (
            <StatusPill tone="muted" size="xs">
              Not connected
            </StatusPill>
          )
        }
        subtext={subtext}
        action={action}
      />
    </div>
  );
}

function DisconnectButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-ink-2 transition-colors hover:bg-bg hover:text-bad"
    >
      Disconnect
    </button>
  );
}

interface LinkCodeBannerProps {
  platform: PlatformDef;
  token: string;
  expiresAt: string;
  /** wa.me deep link with the code pre-filled, when a bot number is configured. */
  deepLink: string | null;
  onDismiss: () => void;
}

function LinkCodeBanner({ platform, token, expiresAt, deepLink, onDismiss }: LinkCodeBannerProps) {
  const [copied, setCopied] = useState(false);

  function copy() {
    void navigator.clipboard?.writeText(token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const isWhatsApp = platform.id === "whatsapp";
  // The QR/scan path is only meaningful on WhatsApp, and only when the backend
  // gave us a deep link (a bot number is configured). Telegram/Slack — and
  // WhatsApp without a configured number — keep the copy-the-code flow.
  const showQr = isWhatsApp && deepLink != null;

  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-sky-800">
            {isWhatsApp ? "Add a WhatsApp number" : `Connect ${platform.label}`}
          </p>
          <p className="mt-0.5 text-xs text-sky-600">
            {showQr ? (
              <>
                Scan this with the phone you want to add — WhatsApp opens with the code ready to
                send. Or open <span className="font-medium">{platform.handle}</span> on that number
                and send the code:
              </>
            ) : isWhatsApp ? (
              <>
                Open <span className="font-medium">{platform.handle}</span> on the number you want to
                add and send this code:
              </>
            ) : (
              <>
                Open <span className="font-medium">{platform.handle}</span> and send this code:
              </>
            )}
          </p>

          <div className="mt-3 flex items-start gap-4">
            {showQr && deepLink && (
              <div className="shrink-0 rounded-md border border-sky-200 bg-paper p-2">
                <QRCodeSVG value={deepLink} size={104} className="block" />
              </div>
            )}
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <span className="select-all font-mono text-2xl font-bold tracking-[0.35em] text-sky-900">
                  {token}
                </span>
                <button
                  type="button"
                  onClick={copy}
                  className="text-xs font-medium text-sky-600 underline underline-offset-2 hover:text-sky-800"
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              {showQr && deepLink && (
                <a
                  href={deepLink}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-sky-700"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Open in WhatsApp
                </a>
              )}
              <p className="mt-2 text-xs text-sky-500">
                Expires at{" "}
                {new Date(expiresAt).toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="shrink-0 text-lg font-light leading-none text-sky-400 hover:text-sky-600"
        >
          ×
        </button>
      </div>
    </div>
  );
}
